import React, { memo, useContext } from 'react';
import { AppContext } from '@prisjakt/ui-app-context';
import { ParamsAndReturnTypes } from '@prisjakt/ui-translate/src/translate-func';
import StockStatus from '@prisjakt/ui-stock-status';

import { StockStatusStates } from '@prisjakt/ui-stock-status/src/types';
import { FontEmphasis } from '@prisjakt/ui/text';
import { ProductPriceType } from '../../../../../../../types';
import { Price, ShippingCollection } from '../../../../../../../../../types';

import Icon from './components/stock-status-icon';
import PriceNotice from '../price-notice';

import * as S from './styles';

type OwnProps = {
  className?: string;
  status?: StockStatusStates | null;
  statusText?: string | null;
  toneDown?: boolean;
  countryCode?: string | null;
  isInternationalPrice?: boolean;
  priceType?: ProductPriceType | null;
  deliveryOptions?: Price['deliveryOptions'];
  width?: number;
  brandRecommendedRetailer?: boolean;
  shipping?: ShippingCollection | null;
};

const Label: React.FC = props => {
  return (
    <S.StyledLabel emphasis={FontEmphasis.MEDIUM}>
      {props.children}
    </S.StyledLabel>
  );
};

const StockStatusColumn = ({
  className,
  status,
  statusText,
  toneDown = false,
  countryCode,
  isInternationalPrice = false,
  priceType,
  deliveryOptions = [],
  width,
  brandRecommendedRetailer,
}: OwnProps) => {
  const { t, market } = useContext(AppContext);
  const processedStatusText = statusText
    ? statusText.replace(/-/g, '\u2011') /* no-breaking hyphen in dates */
    : t(
        'product',
        `stock-status.${status ||
          'unknown'}` as keyof ParamsAndReturnTypes['product']
      );

  const homeDelivery =
    deliveryOptions &&
    deliveryOptions.find(option => option && option.type === 'HOME_DELIVERY');

  return (
    <S.BadgeWrapper className={className}>
      {brandRecommendedRetailer && (
        <S.Badge>
          <Icon
            icon={<S.RecommendedRetailerIcon />}
            tooltipText={t('store', 'brand.recommended-retailer')}
            showTooltip
          />
          <S.DesktopOnly>
            <Label>{t('store', 'brand.recommended-retailer')}</Label>
          </S.DesktopOnly>
        </S.Badge>
      )}

      {priceType && priceType !== ProductPriceType.NORMAL ? (
        <S.Badge>
          <Icon
            icon={
              <PriceNotice
                type={priceType as NonNullable<ProductPriceType>}
                hideText
              />
            }
            tooltipText={t(
              'product',
              `price-notice.${priceType}` as keyof ParamsAndReturnTypes['product']
            )}
            showTooltip
          />
          <Label>
            {t(
              'product',
              `price-notice.${priceType}` as keyof ParamsAndReturnTypes['product']
            )}
          </Label>
        </S.Badge>
      ) : null}

      {homeDelivery && (
        <S.Badge>
          <Icon
            icon={
              <S.HomeDeliveryIcon
                src={`${market.cdn}/rfe/g/home-delivery.e5660c.svg`}
                title={t(
                  'product',
                  `home-delivery.info` as keyof ParamsAndReturnTypes['product']
                )}
              />
            }
            tooltipText={t(
              'product',
              `home-delivery.info` as keyof ParamsAndReturnTypes['product']
            )}
            showTooltip
          />
        </S.Badge>
      )}

      {countryCode && (
        <S.Badge>
          <S.CountryFlag
            show={isInternationalPrice}
            countryCode={countryCode}
            title={t(
              'common',
              `market.${countryCode}` as keyof ParamsAndReturnTypes['common']
            )}
            tooltipPlacement="top"
          />
        </S.Badge>
      )}
      <S.Badge>
        <Icon
          icon={
            <StockStatus
              status={status}
              toneDown={toneDown}
              tooltip={processedStatusText as string}
              tooltipPlacement="top"
              width={width}
            />
          }
        />
        <Label>{processedStatusText}</Label>
      </S.Badge>
    </S.BadgeWrapper>
  );
};

export default memo(StockStatusColumn);
